import Navbar from '../components/Navbar'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const TOC = [
  { id: 'beta-disclaimer',  label: 'Beta disclaimer' },
  { id: 'short-version',    label: 'The short version' },
  { id: 'accepting',        label: 'Accepting these terms' },
  { id: 'your-account',     label: 'Your account' },
  { id: 'age',              label: 'Age requirements' },
  { id: 'what-you-can',     label: 'What you can post' },
  { id: 'what-you-cannot',  label: 'What you cannot post' },
  { id: 'no-ai',            label: 'No AI-generated content' },
  { id: 'ratings',          label: 'Content rating system' },
  { id: 'ip',               label: 'Intellectual property' },
  { id: 'fanfic',           label: 'Fan fiction and copyright' },
  { id: 'violations',       label: 'How we handle violations' },
  { id: 'child-safety',     label: 'Child Safety & CSAM Policy' },
  { id: 'virtual-currency', label: 'Virtual Currency (Verse Coins)' },
  { id: 'purchases',        label: 'Purchases & Refunds' },
  { id: 'tipping',          label: 'Tipping & Author Earnings' },
  { id: 'no-loss',          label: 'Financial responsibility & losses' },
  { id: 'tax',              label: 'Tax Responsibility' },
  { id: 'no-gambling',      label: 'No Gambling or Speculation' },
  { id: 'disclaimer',       label: 'Disclaimer' },
  { id: 'liability',        label: 'Limitation of liability' },
  { id: 'law',              label: 'Governing law' },
  { id: 'beta',             label: 'Beta testing period' },
  { id: 'moderation',       label: 'Platform Administration' },
  { id: 'changes',          label: 'Changes' },
  { id: 'contact',          label: 'Contact' },
]

export default function TermsOfServicePage() {
  return (
    <div className="legal-page">
      <SEO title="Terms of Service" description="Verse terms of service — rules and agreements for using the platform." />
      <Navbar />
      <div className="legal-page__inner">
        <header className="legal-page__header">
          <p className="legal-page__label">Legal</p>
          <h1 className="legal-h1">Terms of Service</h1>
          <p className="legal-page__meta">Last updated: April 2026</p>
        </header>

        <nav className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc__heading">On this page</p>
          <ol className="legal-toc__list">
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="legal-toc__link">{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="beta-disclaimer" className="legal-section">
          <h2 className="legal-h2">Beta disclaimer</h2>
          <div className="legal-callout">
            <p>
              Verse is currently in a beta / soft launch phase. These Terms of Service are preliminary
              and will be updated with formal legal terms before the platform's full launch, including
              the introduction of payment features. By using the platform during this phase, you
              acknowledge that features, functionality, and these terms are subject to change. We will
              notify users of any significant updates. Continued use of the platform after changes are
              posted constitutes acceptance of the revised terms.
            </p>
          </div>
        </section>

        <section id="short-version" className="legal-section">
          <h2 className="legal-h2">The short version</h2>
          <div className="legal-callout">
            <ul className="legal-callout__list">
              <li>You must be 13 or older to use Verse. Users under 18 get a teen account with mature content hidden.</li>
              <li>Verse is a fiction-only platform. Only fictional stories may be published to the browse page.</li>
              <li>You own everything you write. You give us a license to display it on the platform.</li>
              <li>Don't post anything illegal, harmful, or that sexualises minors. Violations of that rule are reported to authorities with zero tolerance.</li>
            </ul>
          </div>
        </section>

        <section id="accepting" className="legal-section">
          <h2 className="legal-h2">Accepting these terms</h2>
          <p>
            Verse is owned and operated by <strong>Verse Interactive Platforms LLC</strong>, a Pennsylvania
            limited liability company and a separate legal entity distinct from any individual. References
            to "we," "us," or "Verse" in these terms refer to Verse Interactive Platforms LLC.
          </p>
          <p>
            By creating an account or using Verse, you agree to these Terms of Service and our <Link to="/privacy" className="legal-link">Privacy Policy</Link>. If you don't agree, please don't use the platform.
          </p>
          <p>
            If you are under 13 years old, you may not use Verse. This is required by U.S. law (COPPA). We check your age during sign-up, and accounts found to belong to users under 13 will be deleted.
          </p>
          <p>
            If you're signing up on behalf of an organisation, you represent that you have the authority to bind that organisation to these terms.
          </p>
        </section>

        <section id="your-account" className="legal-section">
          <h2 className="legal-h2">Your account</h2>
          <ul className="legal-list">
            <li><strong>You're responsible for your account.</strong> Keep your password secure and don't share it with others. If you suspect your account has been compromised, change your password immediately and contact us.</li>
            <li><strong>One account per person.</strong> Each user may only have one account. Creating multiple accounts for any reason — including evading bans, boosting content, or circumventing limitations — is not allowed. Exceptions may only be granted with explicit written permission from a Verse administrator. If you believe you need a second account, contact <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a> to request permission. Unauthorised duplicate accounts will be terminated.</li>
            <li><strong>Accurate information.</strong> You must provide an accurate email address and date of birth when signing up. Providing false information — especially a false age — violates these terms.</li>
            <li><strong>Account names.</strong> Usernames must not impersonate other people, claim to be Verse staff, or use offensive language.</li>
          </ul>
        </section>

        <section id="age" className="legal-section">
          <h2 className="legal-h2">Age requirements</h2>
          <ul className="legal-list">
            <li><strong>13 and older:</strong> The minimum age to use Verse is 13. This is required by COPPA (Children's Online Privacy Protection Act).</li>
            <li><strong>Ages 13–17 (teen accounts):</strong> Teen accounts can use most of Verse normally, but mature-rated content (18+) is automatically hidden and cannot be unlocked. This restriction applies regardless of parental consent.</li>
            <li><strong>Under 13:</strong> If you are under 13, you cannot create an account. Our sign-up form checks your date of birth and will block registration. If we discover an account belongs to someone under 13, we will immediately delete the account and all associated data.</li>
            <li><strong>Reporting underage users:</strong> If you believe a user is under 13, please report them to <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.</li>
          </ul>
        </section>

        <section id="what-you-can" className="legal-section">
          <h2 className="legal-h2">What you can post</h2>
          <p>Verse is a platform for <strong>fiction</strong>. Only fictional stories may be published to the browse page. Non-fiction content — including essays, journalism, tutorials, memoirs, and opinion pieces — is not permitted as a published story.</p>
          <ul className="legal-list">
            <li><strong>Original fiction:</strong> Stories, novellas, poetry, and other fictional creative writing you've written yourself.</li>
            <li><strong>Fan fiction:</strong> Fictional stories based on existing books, films, shows, games, and other creative works. See our <a href="#fanfic" className="legal-link">fan fiction section</a> for more.</li>
            <li><strong>Comments:</strong> Thoughtful responses to stories and conversations with other community members.</li>
            <li><strong>Profile content:</strong> A bio, profile picture, and other information about yourself as a writer or reader.</li>
          </ul>
          <p>
            <strong>You keep ownership of everything you write.</strong> Publishing your work on Verse does not transfer your copyright to us. See the <a href="#ip" className="legal-link">intellectual property section</a> for details.
          </p>
        </section>

        <section id="what-you-cannot" className="legal-section">
          <h2 className="legal-h2">What you cannot post</h2>
          <div className="legal-callout legal-callout--warning">
            <strong>Zero tolerance:</strong> Content that sexualises minors (CSAM or written CSAM) results in an immediate permanent ban and will be reported to the National Center for Missing and Exploited Children (NCMEC) and relevant law enforcement. No exceptions.
          </div>
          <p>
            All users must follow our <Link to="/guidelines" className="legal-link">Content Guidelines</Link>. The guidelines are part of these Terms of Service — violating them is a violation of these terms.
          </p>
          <p>The following content is prohibited on Verse:</p>
          <ul className="legal-list">
            <li><strong>Sexual content involving minors:</strong> Any content — written or visual — that depicts minors in sexual situations. This includes fictional characters if written to be minors in a sexual context.</li>
            <li><strong>Nude or pornographic images:</strong> Uploading nude, sexually explicit, or pornographic images is prohibited. This applies to all image uploads including profile avatars, banners, story covers, group covers, and any other visual content. Verse is a writing platform — sexually explicit written fiction is allowed when properly rated, but explicit imagery is not.</li>
            <li><strong>Harassment and doxxing:</strong> Targeting, threatening, or intimidating other users. Publishing someone's private real-world information (address, phone number, workplace) without consent.</li>
            <li><strong>Spam and manipulation:</strong> Automated posting, artificially inflating likes/views, or using Verse to advertise products or services without authorisation.</li>
            <li><strong>Malware and harmful code:</strong> Links or content designed to harm other users' devices or steal their information.</li>
            <li><strong>Real people's private information:</strong> Publishing private information about real individuals without their consent.</li>
            <li><strong>Illegal content:</strong> Anything that violates applicable law, including copyright infringement, defamation, or content that incites violence.</li>
            <li><strong>Impersonation:</strong> Pretending to be another person, author, or Verse staff in a way that could mislead others.</li>
          </ul>
          <h3 className="legal-h3">Group content</h3>
          <p>
            Groups on Verse are open to all users including minors. All content posted in groups — including roleplay, stories, comments, and discussions — must be appropriate for a general audience. Sexually explicit content, adult roleplay, and graphic material are prohibited in groups regardless of the age of participants. Mature or explicit creative writing must be published as a properly rated story, not posted in a group.
          </p>
        </section>

        <section id="no-ai" className="legal-section">
          <h2 className="legal-h2">No AI-generated content</h2>
          <div className="legal-callout legal-callout--warning">
            <strong>Verse is a platform for human creativity.</strong> AI-generated stories, chapters, and other written content are not allowed and will be removed.
          </div>
          <p>
            Verse exists to celebrate human writers and their craft. To protect the integrity of the platform and the trust between authors and readers, the following rules apply:
          </p>
          <ul className="legal-list">
            <li><strong>No AI-written stories or chapters:</strong> You may not publish stories, chapters, poems, or other creative writing that was generated — in whole or in substantial part — by AI tools such as ChatGPT, Claude, Gemini, or similar large language models. This includes content that is AI-generated and then lightly edited by a human.</li>
            <li><strong>No AI-generated profile content:</strong> Bios, comments, group posts, and messages must be written by you, not generated by AI.</li>
            <li><strong>AI as a tool, not an author:</strong> Using AI for brainstorming ideas, checking grammar/spelling, or getting feedback on your own writing is fine. The key distinction is that <em>you</em> must be the author — AI can assist your process, but it cannot write your content.</li>
            <li><strong>Disclosure:</strong> If AI was used in any assistive capacity (e.g. grammar checking, translation assistance), we encourage you to mention it in your author notes. This is not required but is appreciated by readers.</li>
          </ul>
          <p>
            <strong>How we handle violations:</strong> Stories suspected of being AI-generated may be flagged by readers or staff. We will review flagged content and may use detection tools as part of our review process. If content is determined to be AI-generated, it will be removed. Repeated violations will result in account suspension or a permanent ban.
          </p>
          <p>
            We understand that the line between "AI-assisted" and "AI-generated" can sometimes be blurry. When in doubt, ask yourself: <em>did I write this, or did a machine?</em> If a machine wrote it, it doesn't belong on Verse.
          </p>
        </section>

        <section id="ratings" className="legal-section">
          <h2 className="legal-h2">Our content rating system</h2>
          <p>Every story on Verse is tagged with a content rating. Authors are responsible for rating their work accurately.</p>
          <div className="legal-table-wrap">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Rating</th>
                  <th>Who can read</th>
                  <th>What it means</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Teen</strong></td>
                  <td>13+ (all accounts)</td>
                  <td>May contain mild violence, romantic themes, and moderate language. Not sexually explicit.</td>
                </tr>
                <tr>
                  <td><strong>Mature</strong></td>
                  <td>Adults (18+) only</td>
                  <td>May contain explicit content, graphic violence, or adult themes. Requires age confirmation. Hidden for teen accounts.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Stories rated Mature may optionally be marked as <strong>sexually explicit</strong> by the author. The tipping feature is completely hidden on sexually explicit stories — readers will not see a tip option and no tips can be sent. Authors must accurately flag sexually explicit content; deliberately omitting the flag to receive tips on pornographic content is a violation of these terms and will result in content removal and potential account action.
          </p>
          <p>
            <strong>Tip refunds on mislabelled content:</strong> If a story is later flagged as sexually explicit (by the author or by Verse staff), all tips previously sent on that story will be automatically refunded to the original tippers. Refunded coins are returned as purchased coins. The corresponding amount will be deducted from the author's earned balance. Authors who repeatedly mislabel sexually explicit content to receive tips may face account suspension or a permanent ban.
          </p>
          <p>
            Deliberately mis-rating content to bypass restrictions (e.g. rating adult content as "Teen") is a violation of these terms and will result in content removal and potential account action.
          </p>
        </section>

        <section id="ip" className="legal-section">
          <h2 className="legal-h2">Intellectual property</h2>
          <h3 className="legal-h3">Your content</h3>
          <p>
            You own your writing. Publishing on Verse does not change that. By posting content on Verse, you grant us a non-exclusive, worldwide, royalty-free licence to display, reproduce, and distribute your content on the Verse platform and for purposes related to operating the service (such as showing your story in search results or sharing it on social media with attribution). You can revoke this licence by deleting your content or account.
          </p>

          <h3 className="legal-h3">Verse's brand</h3>
          <p>
            The Verse name, logo, and design are owned by Verse. You may not use them in ways that suggest an official affiliation or endorsement without our written permission.
          </p>

          <h3 className="legal-h3">DMCA takedowns</h3>
          <p>
            If you believe content on Verse infringes your copyright, you can submit a DMCA takedown notice to <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>. Please include: a description of the copyrighted work, a link to the infringing content, your contact information, and a statement that you have a good-faith belief the use is infringing. We will investigate and respond promptly.
          </p>

          <h3 className="legal-h3">DMCA counter-notification</h3>
          <p>
            If your content was removed due to a DMCA takedown notice and you believe the removal was a mistake or that you have the right to use the material, you may submit a counter-notification to <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>. Your counter-notification must include:
          </p>
          <ul className="legal-list">
            <li>Your name, address, and phone number.</li>
            <li>A link or description of the content that was removed.</li>
            <li>A statement under penalty of perjury that you have a good-faith belief the content was removed by mistake or misidentification.</li>
            <li>A statement that you consent to the jurisdiction of the federal court in your district and that you will accept service of process from the person who filed the original takedown notice.</li>
            <li>Your physical or electronic signature.</li>
          </ul>
          <p>
            Upon receiving a valid counter-notification, we will forward it to the original complainant. If the complainant does not file a court action within 10 business days, we will restore the removed content. Please note that filing a false counter-notification may result in legal liability.
          </p>
        </section>

        <section id="fanfic" className="legal-section">
          <h2 className="legal-h2">Fan fiction and copyright</h2>
          <p>
            Fan fiction is a beloved creative tradition with a long history. Writing stories about characters from books, shows, films, and games you love is generally considered a fair and transformative use of those works, and we celebrate that tradition on Verse.
          </p>
          <p>However, there are some important guidelines:</p>
          <ul className="legal-list">
            <li><strong>Tag your work clearly.</strong> Always identify your fan fiction as derivative work, including the original source in your story's fandom tag.</li>
            <li><strong>Non-commercial:</strong> Fan fiction on Verse must be non-commercial. Don't charge readers to access fan fiction of copyrighted works.</li>
            <li><strong>Respect author wishes:</strong> Some authors have publicly asked that fan fiction not be written about their work. We ask that you respect those wishes.</li>
            <li><strong>Original characters:</strong> While you can base characters on real people (celebrities, athletes, etc.) in clearly fictional settings, never write content that could be mistaken for fact or that portrays them in a harmful or defamatory way.</li>
            <li><strong>No verbatim copying:</strong> You may not copy and reproduce substantial portions of copyrighted text verbatim.</li>
          </ul>
        </section>

        <section id="violations" className="legal-section">
          <h2 className="legal-h2">How we handle violations</h2>
          <p>When we receive reports of content that violates these terms, we investigate and may take the following actions:</p>
          <ul className="legal-list">
            <li><strong>Warning:</strong> For minor or first-time violations, we may issue a warning and ask you to correct the issue.</li>
            <li><strong>Content removal:</strong> We may remove individual stories, chapters, or comments that violate our policies.</li>
            <li><strong>Account suspension:</strong> Repeated or serious violations may result in a temporary suspension of your account.</li>
            <li><strong>Permanent ban:</strong> Severe violations — especially content that sexualises minors, threats of violence, or deliberate large-scale abuse — will result in a permanent ban with no appeal.</li>
            <li><strong>Reporting to authorities:</strong> We are legally required to report certain content (particularly CSAM) to the National Center for Missing and Exploited Children and relevant law enforcement. We will do so without hesitation.</li>
          </ul>

          <h3 className="legal-h3">Administrative discretion</h3>
          <p>
            Verse administrators and moderators may, at their sole discretion, take any of the following actions to ensure compliance with these Terms of Service:
          </p>
          <ul className="legal-list">
            <li><strong>Edit content:</strong> Administrators and moderators may edit stories, quizzes, profile information, and any other user-generated content to bring it into compliance with these terms. This includes but is not limited to correcting content ratings, removing prohibited material, and updating metadata.</li>
            <li><strong>Hide content:</strong> Administrators and moderators may hide or unpublish stories, quizzes, profiles, and other content that violates these terms. Hidden content will not be visible to other users but may be retained for review purposes.</li>
            <li><strong>No prior notice required:</strong> These actions may be taken without prior notice to the user. Where practical, we will notify you after the fact and explain the reason for the action.</li>
          </ul>
          <p>
            By using Verse, you acknowledge and agree that administrators and moderators have the authority to take these actions at their sole discretion. Decisions made by administrators are final, though you may appeal by contacting <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.
          </p>
        </section>

        <section id="termination" className="legal-section">
          <h2 className="legal-h2">Termination of Service</h2>
          <p>
            We reserve the right to suspend or terminate your account and your access to the service at our sole discretion, at any time, and without prior notice or liability, for any reason whatsoever, including but not limited to a breach of these Terms.
          </p>
        </section>

        <section id="child-safety" className="legal-section">
          <h2 className="legal-h2">Child Safety &amp; CSAM Policy</h2>
          <p>
            Verse has zero tolerance for child sexual abuse material (CSAM) or any content that sexually exploits minors. This includes images, text, and any other content that depicts, promotes, or facilitates the sexual exploitation of children.
          </p>
          <p>
            We actively scan all uploaded images using automated detection systems. If CSAM is detected or reported:
          </p>
          <ul className="legal-list">
            <li>The content will be immediately removed</li>
            <li>The user's account will be permanently suspended without prior notice</li>
            <li>All relevant information will be reported to the National Center for Missing &amp; Exploited Children (NCMEC) and law enforcement authorities as required by United States federal law (18 U.S.C. § 2258A)</li>
            <li>We will cooperate fully with law enforcement investigations</li>
          </ul>
          <p>
            Users who encounter any content they believe may constitute CSAM should report it immediately using the in-app report system or by contacting <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a>.
          </p>
          <p>
            We reserve the right to preserve account data and evidence related to CSAM investigations as required by law.
          </p>
        </section>

        {/*
          DRAFT input for Kost revision pass — NOT final wording.
          This section is one input to the broader ToS revision workstream
          (see user.md "Pre-launch blockers — non-code"). Kost reviews this
          and the surrounding sections (CSAM language, Verse Coins, age
          representation, DMCA) before release. Code merge is not gated on
          this; release is.
        */}
        <section id="account-deletion" className="legal-section">
          <h2 className="legal-h2">Account Deletion</h2>
          <p>
            You may delete your account at any time from Settings. When you do,
            your profile, content, and personal data are immediately hidden
            from public view. Your username is reserved against your account
            during this period.
          </p>
          <p>
            After 180 days, your account and all associated data are
            permanently deleted and cannot be recovered. Verse Coins are
            forfeit immediately upon initiating deletion and are not restored
            if you choose to restore your account within the 180-day window.
          </p>
          <p>
            You may restore your account at any time within the 180-day window
            by signing back in with the same credentials. Frozen accounts
            (those subject to CSAM-related review) cannot be deleted by the
            account holder; contact <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a> if you believe your
            account is incorrectly frozen.
          </p>
        </section>

        <section id="virtual-currency" className="legal-section">
          <h2 className="legal-h2">Virtual Currency (Verse Coins)</h2>
          <ul className="legal-list">
            <li>Verse Coins are a virtual currency used within the Verse platform.</li>
            <li>Coins have no real-world monetary value outside of the platform.</li>
            <li>Coins are non-transferable between users except through the tipping feature.</li>
            <li>Coins cannot be exchanged for cash except through the official Author Cashout program.</li>
            <li>Verse reserves the right to modify coin pricing, exchange rates, and availability at any time.</li>
            <li>Unused coins remain in your account indefinitely unless your account is terminated.</li>
            <li>Upon account termination, all coins are forfeited.</li>
          </ul>
        </section>

        <section id="purchases" className="legal-section">
          <h2 className="legal-h2">Purchases &amp; Refunds</h2>
          <ul className="legal-list">
            <li>All Verse Coin purchases are final and non-refundable, except in cases of technical errors, unauthorized transactions, or as required by applicable law.</li>
            <li>Payment processing is handled by Stripe; Verse does not store your credit card or payment information.</li>
            <li>By purchasing coins, you agree to Stripe's terms of service.</li>
            <li>Verse reserves the right to limit purchase amounts and frequency to prevent abuse.</li>
          </ul>
        </section>

        <section id="tipping" className="legal-section">
          <h2 className="legal-h2">Tipping &amp; Author Earnings</h2>
          <ul className="legal-list">
            <li>Readers may tip Verse Coins to authors on published stories.</li>
            <li><strong>Age restrictions:</strong> Users under 18 may purchase coins and send tips to authors. Users under 18 are not eligible to receive tips or request cashouts. Identity verification (KYC) for cashouts requires the user to be at least 18 years old.</li>
            <li>Tips are voluntary. Tips may be automatically refunded if the tipped story is later flagged as sexually explicit (see <a href="#ratings" className="legal-link">Content rating system</a>).</li>
            <li><strong>3-day hold period:</strong> Earned coins from tips are held for 3 days before they become redeemable. This hold period exists to allow time for content review and to protect against mislabelled content. During the hold period, tips may be refunded at no cost to the author. After the hold period, refunded amounts will be deducted from the author's earned balance.</li>
            <li>Verse retains a 30% platform fee on all coin cashouts.</li>
            <li>Authors may request a cashout of their earned coins subject to:
              <ul className="legal-list">
                <li>A minimum threshold of 5,000 coins ($50 USD equivalent).</li>
                <li>Identity verification.</li>
                <li>Compliance with applicable tax laws.</li>
              </ul>
            </li>
            <li>Cashout requests are processed through Stripe Connect.</li>
            <li>Verse does not guarantee specific payout timelines and reserves the right to withhold payouts pending fraud investigation.</li>
          </ul>
        </section>

        <section id="no-loss" className="legal-section">
          <h2 className="legal-h2">Financial responsibility &amp; losses</h2>
          <p>
            Verse is not required to absorb financial losses arising from tip refunds, mislabelled content, fraudulent activity, or any other circumstance. By using the Verse Coins system — whether as a tipper or an author — you acknowledge and agree to the following:
          </p>
          <ul className="legal-list">
            <li><strong>Negative balances:</strong> If an author's earned balance becomes negative due to refunds (for example, when a story is flagged as sexually explicit after receiving tips), the author is solely responsible for that deficit.</li>
            <li><strong>Recovery of losses:</strong> Verse reserves the right to withhold future earnings, suspend cashout privileges, or take other appropriate action to recover any outstanding negative balance.</li>
            <li><strong>No reimbursement:</strong> Verse will not reimburse, compensate, or otherwise make whole any user for losses resulting from refunds, content removal, account suspension, or enforcement of these terms.</li>
            <li><strong>Repeated violations:</strong> Authors who repeatedly incur negative balances or engage in conduct that causes financial loss to the platform may face account suspension or permanent termination.</li>
          </ul>
        </section>

        <section id="tax" className="legal-section">
          <h2 className="legal-h2">Tax Responsibility</h2>
          <ul className="legal-list">
            <li>Authors are solely responsible for reporting and paying taxes on income earned through Verse Coins.</li>
            <li>For U.S.-based authors earning $600 or more in a calendar year, Verse will issue a Form 1099 as required by the IRS.</li>
            <li>Authors in other jurisdictions are responsible for complying with their local tax obligations.</li>
          </ul>
        </section>

        <section id="no-gambling" className="legal-section">
          <h2 className="legal-h2">No Gambling or Speculation</h2>
          <ul className="legal-list">
            <li>Verse Coins are not a cryptocurrency, security, or financial instrument.</li>
            <li>Coins cannot be wagered, traded between users for cash, resold, or used outside the Verse platform.</li>
            <li>Any attempt to sell, trade, or transfer coins outside of the platform's designated features will result in account termination.</li>
          </ul>
        </section>

        <section id="disclaimer" className="legal-section">
          <h2 className="legal-h2">Disclaimer</h2>
          <p>
            Verse is provided "as is" and "as available" without warranties of any kind. We don't guarantee that the platform will be available 100% of the time, error-free, or that it will meet all of your needs.
          </p>
          <p>
            We are not responsible for the content that users post on Verse. Stories, comments, and other user-generated content represent the views of their authors, not Verse. We provide tools for users to report content that violates our policies and review those reports, but we cannot review every piece of content in advance.
          </p>
        </section>

        <section id="liability" className="legal-section">
          <h2 className="legal-h2">Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, Verse and its operators will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of (or inability to use) the platform. This includes loss of data, loss of profits, or any other harm.
          </p>
          <p>
            In plain English: if something goes wrong — a server outage, lost content, a bug — we're sorry, and we'll do our best to fix it, but our liability is limited. This is standard for online platforms.
          </p>
          <p>
            Some jurisdictions don't allow limitations of liability, so some of the above may not apply to you.
          </p>
        </section>

        <section id="law" className="legal-section">
          <h2 className="legal-h2">Governing law</h2>
          <p>
            These Terms are governed by the laws of the Commonwealth of Pennsylvania, United States, without regard to its conflict of law provisions. Any disputes arising from your use of Verse will be subject to the exclusive jurisdiction of the state and federal courts located in Pennsylvania.
          </p>
          <p>
            If you are located in the European Union, you also have rights under EU consumer protection law and the GDPR that cannot be waived by contract. See our <Link to="/privacy" className="legal-link">Privacy Policy</Link> for your GDPR rights.
          </p>
        </section>

        <section id="beta" className="legal-section">
          <h2 className="legal-h2">Beta testing period</h2>
          <p>
            Verse is currently in a <strong>beta testing phase</strong>. By using Verse during this period, you acknowledge and agree to the following:
          </p>
          <ul className="legal-list">
            <li><strong>Service provided "as is".</strong> During the beta period, features may be incomplete, changed, or removed without notice. The platform may experience downtime, data loss, or unexpected behaviour. We will make reasonable efforts to protect your data but cannot guarantee uninterrupted service.</li>
            <li><strong>Terms may change without advance notice.</strong> Because the platform is in active development, these Terms of Service may be updated at any time during the beta period without the standard 14-day notification period that applies after launch. You expressly waive the right to advance notice of changes to these terms during the beta period.</li>
            <li><strong>Re-acceptance may be required.</strong> When these terms are materially updated — including but not limited to at the time of official launch — you may be required to review and affirmatively re-accept the updated terms before continuing to use Verse. If you do not accept the updated terms, your account will remain but access to the platform will be restricted until acceptance.</li>
            <li><strong>Feedback and testing.</strong> Any feedback, bug reports, or suggestions you provide during the beta period are voluntary and may be used by Verse without compensation or attribution.</li>
            <li><strong>End of beta.</strong> The beta testing period will end upon official public launch of Verse. At that time, a final version of these Terms of Service will be published and all users — including beta testers — will be required to accept the updated terms to continue using the platform. The standard 14-day notification period for future changes will apply after the beta period ends.</li>
          </ul>
          <p>
            This section is governed by the laws of the Commonwealth of Pennsylvania. By continuing to use Verse during the beta period, you consent to this waiver of advance notice for Terms of Service changes.
          </p>
        </section>

        <section id="moderation" className="legal-section">
          <h2 className="legal-h2">Platform Administration</h2>
          <p>
            Platform administrators may access, review, and moderate all content on Verse regardless of privacy settings, including but not limited to: private groups, wall posts, messages reported for review, user profiles with hidden or disabled visibility settings, and online activity status. This access is used solely for content moderation, safety enforcement, and compliance with applicable law.
          </p>
          <p>
            Administrators may freeze accounts and preserve content when required by law, including compliance with federal child safety reporting obligations.
          </p>
        </section>

        <section id="changes" className="legal-section">
          <h2 className="legal-h2">Changes</h2>
          <p>
            We may update these Terms of Service from time to time. After the beta testing period ends, we will notify you by email at least 14 days before significant changes take effect. During the beta period, terms may change at any time as described in the "Beta testing period" section above.
          </p>
          <p>
            When terms are materially updated, you may be required to affirmatively re-accept them. Continued use of Verse after changes take effect means you accept the new terms. If you don't agree with a change, you can close your account before the new terms take effect.
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2 className="legal-h2">Contact</h2>
          <p>
            Questions about these terms? We're happy to help.
          </p>
          <ul className="legal-list">
            <li>General support: <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a></li>
            <li>Privacy &amp; legal: <a href="mailto:support@versesite.com" className="legal-link">support@versesite.com</a></li>
          </ul>
          <p>
            <strong>Verse Interactive Platforms LLC</strong>
          </p>
        </section>
      </div>
    </div>
  )
}
